import { useEffect, useState } from 'react';
import useUserQuery from '../../hooks/queries/useUserQuery';
import { useGetBettieProducts } from '../../../domains/bettie/ui/hooks/useGetBettieProducts';
import { openNewTab } from '../../../utils/UIUtils';
import { getOneTimePaymentLaunchPath, getPgProvider } from '../../../utils/paymentProvider';
import type { PayLetterPaymentMethod } from '../../../domains/orders/services/orderServices';
import type { components } from '../../../types/schema';

type ApiV1BettieProduct = components['schemas']['ApiV1BettieProduct'];

export type BettieChargeMode = 'autoTopup' | 'onetime';

export interface UseBettieChargeOptions {
  onClose?: () => void;
}

export const useBettieCharge = (options?: UseBettieChargeOptions | (() => void)) => {
  const onClose = typeof options === 'function' ? options : options?.onClose;

  const { data: oneTimeProducts, isFetching: isBettieProductsLoading } = useGetBettieProducts({
    category: 'onetime',
    assetType: 'bettie_point'
  });

  const { data: user, refetch: refetchUser } = useUserQuery();

  const [selectedItem, setSelectedItem] = useState<ApiV1BettieProduct | null>(null);

  useEffect(() => {
    const nextDefaultProduct = oneTimeProducts?.[0] || null;
    if (nextDefaultProduct) {
      setSelectedItem(nextDefaultProduct);
    } else {
      setSelectedItem(null);
    }
  }, [oneTimeProducts]);

  const handleSelectItem = (id: string) => {
    const item = oneTimeProducts?.find((product) => product.bettieProductId === id) ?? null;
    setSelectedItem(item);
  };

  useEffect(() => {
    refetchUser();
    const onFocus = () => {
      refetchUser();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchUser]);

  const handlePrimaryClick = (
    paymentMethod?: PayLetterPaymentMethod,
    options?: { chargeMode?: BettieChargeMode }
  ) => {
    if (!selectedItem || !user) return;

    const amount = Number(selectedItem.baseAmount - selectedItem.discountAmount);
    const oneTimePaymentLaunchPath = getOneTimePaymentLaunchPath();
    const isPayLetter = getPgProvider() === 'pay-letter';
    const encodedProductName = encodeURIComponent(selectedItem.name);
    const chargeMode = options?.chargeMode ?? 'onetime';
    const useSubscriptionPaymentFlow =
      selectedItem.category === 'subscription' ||
      selectedItem.category === 'bundle' ||
      chargeMode === 'autoTopup';

    if (useSubscriptionPaymentFlow) {
      if (user?.identified) {
        if (isPayLetter) {
          openNewTab(
            `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}&autoPay=true`
          );
          onClose?.();
          return false;
        }
        openNewTab(
          `/payment/billing?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}`
        );
      } else {
        openNewTab(
          `/identity-verification/intro?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}&isBilling=true${isPayLetter ? '&autoPay=true' : ''}`
        );
      }
      onClose?.();
      return false;
    }

    if (user?.identified) {
      if (isPayLetter && !paymentMethod) {
        return true;
      }
      openNewTab(
        `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}${paymentMethod ? `&paymentMethod=${paymentMethod}` : ''}`
      );
    } else {
      openNewTab(
        `/identity-verification/intro?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}`
      );
    }
    onClose?.();
    return false;
  };

  return {
    /** 구독 카테고리 상품은 더 이상 조회하지 않음(자동 채우기 탭도 단건 상품 사용) */
    subscriptionProducts: [] as ApiV1BettieProduct[],
    oneTimeProducts: oneTimeProducts || [],
    selectedItem,
    handleSelectItem,
    handlePrimaryClick,
    isLoadingProducts: isBettieProductsLoading
  };
};

export default useBettieCharge;
